import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import {
  PiCheckSquareLight,
  PiSquareLight,
  PiSpinnerGapLight,
} from 'react-icons/pi';

interface DeleteTemplateConfirmationProps {
  count?: number;
  entityLabel?: string;          // e.g. "playbook", "template", "table column template"
  entityLabelPlural?: string;    // e.g. "playbooks", "templates"
  description?: string;          // Custom body text; auto-generated if omitted
  showDeleteSessions?: boolean;  // Show "also delete review sessions" checkbox (playbooks only)
  onConfirm: (deleteSessions?: boolean) => Promise<void> | void;
  onCancel: () => void;
}

const DeleteTemplateConfirmation: React.FC<DeleteTemplateConfirmationProps> = ({
  count = 1,
  entityLabel = 'template',
  entityLabelPlural,
  description,
  showDeleteSessions = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSessions, setDeleteSessions] = useState(false);
  const isPlural = count > 1;
  const plural = entityLabelPlural || `${entityLabel}s`;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(showDeleteSessions ? deleteSessions : undefined);
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <PiSpinnerGapLight className="w-12 h-12 text-red-500 animate-spin" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t(`Deleting ${count} ${plural}...`)
              : t(`Deleting ${entityLabel}...`)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  const defaultDescription = isPlural
    ? t(`This will permanently delete the selected ${plural}. This action cannot be undone.`)
    : t(`This will permanently delete this ${entityLabel}. This action cannot be undone.`);

  return (
    <div className="flex flex-col gap-4">
      <div className="p-4 space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {description || defaultDescription}
        </p>

        {showDeleteSessions && (
          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
            onClick={() => setDeleteSessions(!deleteSessions)}
          >
            <div className="mt-0.5 flex-shrink-0">
              {deleteSessions ? (
                <PiCheckSquareLight className="w-5 h-5 text-red-500" />
              ) : (
                <PiSquareLight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {t('Also delete all review sessions')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('Review sessions linked to this playbook will be soft-deleted. You can restore them later if needed.')}
              </p>
            </div>
          </label>
        )}
      </div>

      <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-gray-300 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteTemplateConfirmation;
